import { useCallback, useEffect, useRef, useState } from 'react'
import type { SessionUser } from '@raporto/shared'
import { api, clearCookieJar, type Sprung } from './net/apiClient.js'
import { ApiError } from './net/errors.js'
import {
  db,
  bindeAn,
  clearOnLogout,
  ensureSchema,
  getMeta,
  sperrspurLoeschen,
  META_KEYS
} from './net/localDb.js'
import { start, stop, syncNow, subscribe, type SyncStatus } from './net/syncEngine.js'
import { ActionButton } from './ui/ActionButton.js'
import { useEinstellungen } from './ui/hooks.js'
import { ConnectionBar } from './ui/ConnectionBar.js'
import { Toasts, notify, notifyError, clearToasts } from './ui/Toasts.js'
import { Landing } from './screens/Landing.js'
import { Preise } from './screens/Preise.js'
import { Funktionen } from './screens/Funktionen.js'
import { Branchen } from './screens/Branchen.js'
import { Faq } from './screens/Faq.js'
import { Kontakt } from './screens/Kontakt.js'
import { Markenseite } from './screens/Markenseite.js'
import { Login } from './screens/Login.js'
import { Bestellung } from './screens/Bestellung.js'
import { Checkoutrueckkehr } from './screens/Checkoutrueckkehr.js'
import { Zahlung } from './screens/Zahlung.js'
import { Zugang } from './screens/Zugang.js'
import { Gemeinschaftseinladung } from './screens/Gemeinschaftseinladung.js'
import { Recht } from './screens/Recht.js'
import { Sperre } from './ui/Sperre.js'
import { Customers } from './screens/Customers.js'
import { OfficeCustomers, type Vorgabe } from './screens/OfficeCustomers.js'
import { OfficeProjects } from './screens/OfficeProjects.js'
import { OfficeCatalog } from './screens/OfficeCatalog.js'
import { Projects } from './screens/Projects.js'
import { Catalog } from './screens/Catalog.js'
import { Settings } from './screens/Settings.js'
import { WorkerHome } from './screens/WorkerHome.js'
import { Anmeldelink } from './screens/Anmeldelink.js'
import { Rapports } from './screens/Rapports.js'
import { Documents } from './screens/Documents.js'
import { OpenWork } from './screens/OpenWork.js'
import { ExpressInvoice } from './screens/ExpressInvoice.js'
import { Marke } from './screens/Marke.js'
import { Dashboard } from './screens/Dashboard.js'
import { Security } from './screens/Security.js'
import { MeinRaporto } from './screens/MeinRaporto.js'
import { ControlPlane } from './screens/Control.js'
import { Payments } from './screens/Payments.js'
import { Quotes } from './screens/Quotes.js'
import { OfficeShell, OfficeHead } from './ui/OfficeShell.js'
import { OfficeBetreuung } from './screens/OfficeBetreuung.js'
import { OfficeGemeinschaft } from './screens/OfficeGemeinschaft.js'
import './office.css'
import './public.css'

/**
 * Gerüst und Navigation.
 *
 * Die Navigation ist bewusst flach: vier Bereiche, immer sichtbar, kein Menü
 * zum Aufklappen. Auf einer Baustelle mit Handschuhen ist jeder zusätzliche
 * Tipp ein Tipp zu viel – und ein verstecktes Menü ist genau das.
 *
 * Kein Bildschirm ruft den Server direkt auf: alles geht über apiClient,
 * Cache und Ausgangskorb.
 */

type Tab =
  | 'start'
  | 'dashboard'
  | 'express'
  | 'expressofferte'
  | 'offen'
  | 'rapporte'
  | 'rechnungen'
  | 'offerten'
  | 'zahlungen'
  | 'kunden'
  | 'projekte'
  | 'betreuung'
  | 'gemeinschaft'
  | 'katalog'
  | 'einstellungen'
  | 'team'
  | 'sicherheit'
  | 'meinraporto'
  | 'status'
  | 'betreiber'

interface TabDef {
  id: Tab
  label: string
  superuserOnly?: boolean
  workerOnly?: boolean
}

/**
 * Die Reiter stehen in der Reihenfolge, in der die jeweilige Rolle sie
 * braucht. Ein Monteur startet beim Erfassen, ein Superuser bei dem, was von
 * der Baustelle hereinkommt – Kunden und Katalog pflegt er selten.
 */
const TABS: TabDef[] = [
  { id: 'start', label: 'Start', workerOnly: true },
  // Für das Büro der erste Reiter: die Frage «was muss ich heute anfassen»
  // steht vor jeder anderen.
  { id: 'offen', label: 'Was ist offen', superuserOnly: true },
  { id: 'rapporte', label: 'Rapporte' },
  { id: 'rechnungen', label: 'Rechnungen', superuserOnly: true },
  { id: 'kunden', label: 'Kunden', superuserOnly: true },
  { id: 'projekte', label: 'Projekte' },
  /*
    «Betreuung» nur fuer die Kita.

    Der Reiter steht in dieser Liste, damit die Brotkrume und der Rahmen den
    Bereich kennen; sichtbar wird er erst, wenn `/betreuung/verfuegbar` ja
    sagt. Der Schutz liegt am Server – jede Betreuungsroute weist eine andere
    Branche mit 403 ab.
  */
  { id: 'betreuung', label: 'Betreuung', superuserOnly: true },
  { id: 'katalog', label: 'Leistungen' },
  { id: 'einstellungen', label: 'Einstellungen', superuserOnly: true },
  /*
    Sicherheit gilt für jede Rolle.

    Der Reiter stand bis hierher überhaupt nicht in dieser Liste: der
    Sicherheitsbildschirm war nur über das Warnband erreichbar, und das Band
    gab es nur für den Superuser. Der Server hätte jedem Monteur einen zweiten
    Faktor erlaubt – die Oberfläche hat ihn ihm nie angeboten.

    Das war die eigentliche Lücke, nicht die Regel dahinter: Passwort ändern,
    offene Anmeldungen beenden und der zweite Faktor sind Sachen der Person,
    nicht der Rolle. Ein Monteur hat ein Passwort, ein Telefon und Zugriff auf
    Kundenadressen wie alle anderen auch.
  */
  { id: 'sicherheit', label: 'Sicherheit' },
  /*
    «Mein Raporto» – das eigene Abo, und zwar sichtbar.

    Nur für den Inhaber: Wer Rapporte schreibt, hat mit dem Abo nichts zu tun,
    und ein Reiter, den man sieht und nicht darf, ist schlechter als keiner.

    Bewusst ein eigener Punkt und kein Unterpunkt der Einstellungen: Was der
    Betrieb bei Raporto gebucht hat, ist keine Einstellung seiner Software.
    Zwischen Zahlungskonditionen und Leistungskatalog fände es niemand.
  */
  { id: 'meinraporto', label: 'Mein Raporto', superuserOnly: true },
  /*
    «Kita-Gemeinschaft» – der gemeinsame Auftritt mehrerer Inhaberinnen.

    Nur für den Inhaber, und nur wo es eine Gemeinschaft gibt oder eingerichtet
    werden soll. Der Bereich verwaltet Name, Logo, Adresse und Mitgliedschaften –
    keine Geschäftsdaten. Die Zusicherung steht am Server.
  */
  { id: 'gemeinschaft', label: 'Kita-Gemeinschaft', superuserOnly: true },
  { id: 'status', label: 'Status' }
]

/**
 * Vier Orte statt einem.
 *
 *   /        die öffentliche Übersicht – was ist Raporto
 *   /preise  was es kostet, vollständig
 *   /login   die Anmeldung
 *   /app     die Software
 *
 * Bewusst ohne Router-Bibliothek: es sind vier Pfade, und eine Abhängigkeit
 * mehr im Bündel kostet mehr als die zwanzig Zeilen hier. Der Browserverlauf
 * funktioniert trotzdem – Vor und Zurück landen dort, wo sie sollen.
 */
type Ort =
  | 'start'
  | 'preise'
  | 'funktionen'
  | 'branchen'
  | 'faq'
  | 'kontakt'
  | 'marke'
  | 'bestellen'
  | 'zahlung'
  | 'willkommen'
  | 'login'
  | 'app'
  | 'recht'
  | 'zugang'
  | 'anmeldelink'
  | 'gemeinschaftseinladung'

function ortAusPfad(pfad: string): Ort {
  /*
    Die beiden Link-Pfade zuerst.

    Sie kommen aus einer E-Mail und treffen jemanden, der nicht angemeldet ist
    und es gerade auch nicht sein kann – steht die Abfrage weiter unten, landet
    er auf dem Anmeldeformular, und der Link, für den er hierhergekommen ist,
    ist verbraucht, ohne benutzt worden zu sein.
  */
  if (pfad.startsWith('/passwort-neu') || pfad.startsWith('/einladung')) return 'zugang'
  /*
    Der Anmeldelink kommt ebenfalls aus einer E-Mail und gehört deshalb hierher
    – vor jede Abfrage, die eine Sitzung voraussetzt. Der Server antwortet mit
    404, solange der Betreiber ihn nicht eingeschaltet hat; die Seite zeigt das
    dann als abgelaufenen Link an, und das ist die richtige Auskunft.
  */
  if (pfad.startsWith('/anmelden')) return 'anmeldelink'
  /*
    Die Einladung in eine Kita-Gemeinschaft – aus einer Mail, an jemanden,
    der noch keinen Zugang hat. Deshalb hier oben, vor allem, was eine
    Sitzung voraussetzt.
  */
  if (pfad.startsWith('/gemeinschaft')) return 'gemeinschaftseinladung'
  if (pfad.startsWith('/preise')) return 'preise'
  if (pfad.startsWith('/funktionen')) return 'funktionen'
  if (pfad.startsWith('/branchen')) return 'branchen'
  if (pfad.startsWith('/faq')) return 'faq'
  if (pfad.startsWith('/kontakt')) return 'kontakt'
  if (pfad.startsWith('/marke')) return 'marke'
  /*
    Die Zahlungsseite steht unter `/bestellen/…` und muss deshalb VOR
    `/bestellen` abgefragt werden – sonst greift der kürzere Pfad zuerst, und
    die Zahlung landet wieder im Formular.
  */
  if (pfad.startsWith('/bestellen/zahlung')) return 'zahlung'
  if (pfad.startsWith('/bestellen')) return 'bestellen'
  /*
    Die Rückkehr aus dem Stripe-Checkout.

    Sie steht vor `/login` und vor jeder Abfrage, die eine Sitzung
    voraussetzt: Wer hier ankommt, hat gerade bezahlt und ist noch nicht
    angemeldet – das ist ja genau der Zustand, den diese Seite auflöst.
  */
  if (pfad.startsWith('/willkommen')) return 'willkommen'
  if (pfad.startsWith('/login')) return 'login'
  if (pfad.startsWith('/app')) return 'app'
  if (RECHTS_PFADE.some((p) => pfad.startsWith(p.pfad))) return 'recht'
  return 'start'
}

/*
  Jeder Rechtstext hat einen eigenen, sprechenden Pfad.

  Nicht /recht?doc=agb: ein Betrieb, der seinem Treuhänder die AGB schickt,
  schickt einen Verweis, und der soll lesbar sein und bleiben. Ein Pfad mit
  Fragezeichen wird beim Kopieren gern abgeschnitten.
*/
const RECHTS_PFADE: Array<{ id: string; pfad: string }> = [
  { id: 'agb', pfad: '/agb' },
  { id: 'datenschutz', pfad: '/datenschutz' },
  { id: 'avv', pfad: '/auftragsbearbeitung' },
  { id: 'impressum', pfad: '/impressum' }
]

function rechtAusPfad(pfad: string): string {
  return RECHTS_PFADE.find((p) => pfad.startsWith(p.pfad))?.id ?? 'agb'
}

/**
 * Braucht dieser Ort eine Sitzung?
 *
 * Die Frage entscheidet, ob beim Laden `GET /me` losgeht. Bisher ging es
 * immer los – auch auf der Startseite, den Rechtstexten und der Preisseite.
 * Für jeden Besucher ohne Anmeldung, also für praktisch jeden Besucher,
 * stand danach `GET /me 401 (Unauthorized)` in der Konsole, und der Server
 * hatte eine Abfrage auf die Sitzungstabelle gemacht, deren Ergebnis niemand
 * brauchte.
 *
 * Eine öffentliche Seite hängt an keiner Sitzung: sie zeigt für alle
 * dasselbe. Gefragt wird deshalb nur dort, wo die Antwort etwas ändert – im
 * angemeldeten Bereich und auf der Anmeldung, wo eine bestehende Sitzung
 * bedeutet, dass das Formular gar nicht erst nötig ist.
 *
 * `zugang` steht bewusst nicht in der Liste: Passwort-Rücksetzung und
 * Einladung kommen aus einer E-Mail und treffen jemanden, der gerade keine
 * Sitzung hat und auch keine haben soll.
 */
function brauchtSitzung(ort: Ort): boolean {
  return ort === 'app' || ort === 'login'
}

const PFADE: Record<Ort, string> = {
  start: '/',
  preise: '/preise',
  funktionen: '/funktionen',
  branchen: '/branchen',
  faq: '/faq',
  kontakt: '/kontakt',
  marke: '/marke',
  bestellen: '/bestellen',
  zahlung: '/bestellen/zahlung',
  /* Wird nie direkt angesteuert – die Adresse kommt von Stripe, samt Anhang. */
  willkommen: '/willkommen',
  login: '/login',
  app: '/app',
  /* Wird nie direkt benutzt – der Rechtsweg setzt seinen Pfad selbst. */
  recht: '/agb',
  /* Ebenfalls nie direkt: der Pfad kommt samt Token aus der E-Mail. */
  zugang: '/passwort-neu',
  anmeldelink: '/anmelden',
  /* Ebenfalls nie direkt: der Pfad kommt samt Schlüssel aus der Einladungsmail. */
  gemeinschaftseinladung: '/gemeinschaft'
}

export function App(): JSX.Element {
  const [session, setSession] = useState<SessionUser | null>(null)
  /*
    Ob dieses Konto Raporto betreibt – die Antwort kommt mit GET /me.

    Nur für die Anzeige des Menüeintrags. Wer die Flagge im Browser von Hand
    auf true setzt, sieht den Eintrag und bekommt von der Route ein 403.
  */
  const [istBetreiber, setIstBetreiber] = useState(false)
  /*
    Sitzt die Sitzung auf dem Plattformbetrieb?

    Vorgabe false: Die Flagge nimmt einen Bereich weg, und solange die Antwort
    aussteht, gilt der Normalfall – ein Kundenbetrieb mit seinem Abo.
  */
  const [istPlattform, setIstPlattform] = useState(false)
  /* Wie lange die Sitzung laut Server unbenutzt war, aus `GET /me`. */
  const [inaktivMs, setInaktivMs] = useState<number | undefined>(undefined)
  /*
    Der Sperrentscheid – `null`, solange er nicht gefallen ist.
    Gemeldet von `<Sperre>`; ausgewertet unten beim Start des Abgleichs.
  */
  const [sperrzustand, setSperrzustand] = useState<boolean | null>(null)
  const [loading, setLoading] = useState(true)
  const [ort, setOrt] = useState<Ort>(() => ortAusPfad(window.location.pathname))
  const [rechtId, setRechtId] = useState<string>(() => rechtAusPfad(window.location.pathname))
  /*
    Welches Paket auf der Preisseite angeklickt wurde.

    Es steht in der Adresse und nicht nur im Zustand: wer den Verweis weitergibt
    oder die Seite neu lädt, landet sonst wieder beim mittleren Paket – und
    wundert sich, warum seine Wahl weg ist.
  */
  const [paketWunsch, setPaketWunsch] = useState<string>(
    () => new URLSearchParams(window.location.search).get('paket') ?? 'pro'
  )

  /* Vor und Zurück im Browser sollen dasselbe tun wie die Knöpfe. */
  useEffect(() => {
    const beiWechsel = (): void => {
      setOrt(ortAusPfad(window.location.pathname))
      setRechtId(rechtAusPfad(window.location.pathname))
    }
    window.addEventListener('popstate', beiWechsel)
    return () => window.removeEventListener('popstate', beiWechsel)
  }, [])

  /*
    Der Fussbereich verweist auf Pfade, nicht auf Orte.

    Er steht auf mehreren Seiten und soll nicht sieben Rückrufe entgegennehmen,
    von denen jede Seite drei vergisst. Ein Pfad ist das, was in einem Verweis
    steht; die Zuordnung zum Ort macht dieselbe Funktion, die auch beim
    Seitenaufruf aus der Adresszeile liest – zwei Tabellen für dieselbe Frage
    wären die nächste Stelle, die auseinanderläuft.
  */
  const aufPfad = useCallback(
    (pfad: string): void => {
      const ziel = ortAusPfad(pfad)
      if (ziel === 'recht') {
        aufRecht(rechtAusPfad(pfad))
        return
      }
      geheZu(ziel)
    },
    /* geheZu und aufRecht sind selbst stabil; die Abhängigkeiten stehen unten. */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  /* Was die Kopfleiste auf jeder Seite braucht – einmal geschrieben. */
  const glasKopf = {
    aufStart: () => geheZu('start'),
    aufPfad,
    aufAnmelden: () => geheZu(session ? 'app' : 'login'),
    aufStarten: () => (session ? geheZu('app') : bestellen('pro'))
  }

  const geheZu = useCallback((ziel: Ort, anhang = ''): void => {
    const pfad = PFADE[ziel] + anhang
    if (window.location.pathname + window.location.search !== pfad) {
      window.history.pushState({}, '', pfad)
    }
    setOrt(ziel)
    window.scrollTo(0, 0)
  }, [])

  const aufRecht = useCallback((id: string): void => {
    const pfad = RECHTS_PFADE.find((p) => p.id === id)?.pfad ?? '/agb'
    if (window.location.pathname !== pfad) window.history.pushState({}, '', pfad)
    setRechtId(id)
    setOrt('recht')
    window.scrollTo(0, 0)
  }, [])

  const bestellen = useCallback(
    (paket: string): void => {
      setPaketWunsch(paket)
      geheZu('bestellen', `?paket=${encodeURIComponent(paket)}`)
    },
    [geheZu]
  )

  /* Der lokale Zwischenspeicher wird einmal geöffnet – unabhängig davon, wo
     jemand einsteigt. */
  useEffect(() => {
    void ensureSchema()
  }, [])

  /*
    Die Sitzung wird geprüft, wenn der Ort sie braucht – und nur dann.

    Einmal je Sitzungsprüfung, nicht je Ortswechsel: `geprueft` merkt sich,
    dass die Frage gestellt wurde. Wer von der Preisseite zur Anmeldung geht,
    fragt einmal; wer danach in der Anwendung zwischen Bildschirmen wechselt,
    fragt nicht noch einmal.
  */
  /*
    Das bei der Bestellung gewaehlte Paket – nur fuer den ersten Bildschirm
    nach der Registrierung. Es lebt hier oben, weil es einen Wechsel der
    Ansicht ueberdauern muss: geklickt wird in der Bestellung, gebraucht wird
    es im angemeldeten Bereich.
  */
  const [startPaket, setStartPaket] = useState<string | null>(null)
  /*
    Was die eingebettete Kasse braucht – im Zustand und nicht in der Adresse.

    `clientSecret` öffnet eine Zahlungsmaske. In der Adresszeile stünde es im
    Verlauf, in Lesezeichen und im Referrer der nächsten Seite; im Zustand
    steht es nur, solange dieser Tab offen ist.
  */
  const [kasse, setKasse] = useState<{
    clientSecret: string
    betrieb: string
    publicKey: string
    sandbox: boolean
  } | null>(null)

  const geprueft = useRef(false)
  useEffect(() => {
    if (!brauchtSitzung(ort) || session || geprueft.current) {
      setLoading(false)
      return
    }
    geprueft.current = true
    setLoading(true)
    void (async () => {
      try {
        const r = await api.me()
        setSession(r.session as SessionUser)
        setIstBetreiber(r.betreiber === true)
        setIstPlattform(r.plattformbetrieb === true)
        /* Die zweite Quelle für den Sperrbildschirm – siehe ui/Sperre.tsx. */
        setInaktivMs(typeof r.inaktivMs === 'number' ? r.inaktivMs : undefined)
      } catch {
        setSession(null)
      } finally {
        setLoading(false)
      }
    })()
  }, [ort, session])

  /*
    Die Betreiberflagge hängt am Konto, nicht am Seitenaufruf.

    Die Prüfung oben läuft nur, solange NOCH keine Sitzung da ist – sie holt
    die Sitzung aus dem Mitgebrachten. Wer sich gerade eben angemeldet hat,
    kommt aber über `setSession(s)` aus dem Anmeldeformular herein und läuft
    an ihr vorbei; die Flagge wäre dann bis zum nächsten Neuladen falsch, und
    der Betreiber sähe seinen eigenen Bereich nicht.

    Deshalb hier eine eigene Frage, die am Konto hängt: sobald ein anderes
    Konto angemeldet ist, wird sie neu gestellt. Ein Abmelden setzt sie
    zurück, damit nicht das nächste Konto im selben Fenster die Flagge des
    vorherigen erbt.
  */
  const flaggeFuer = useRef<string | null>(null)
  useEffect(() => {
    const konto = session?.userId ?? null
    if (!konto) {
      flaggeFuer.current = null
      setIstBetreiber(false)
      setIstPlattform(false)
      return
    }
    if (flaggeFuer.current === konto) return
    flaggeFuer.current = konto
    void (async () => {
      try {
        const r = await api.me()
        setIstBetreiber(r.betreiber === true)
        setIstPlattform(r.plattformbetrieb === true)
      } catch {
        /* Keine Antwort heisst: kein Bereich. Der Schutz liegt ohnehin am Server. */
        setIstBetreiber(false)
        /*
          Und keine Antwort heisst hier NICHT «Plattformbetrieb».

          Die Flagge nimmt dem Benutzer einen Bereich weg. Sie bei einer
          gescheiterten Abfrage auf true zu setzen, hiesse, einem Kunden sein
          Abo wegen einer Netzstörung zu verstecken.
        */
        setIstPlattform(false)
      }
    })()
  }, [session?.userId])

  /*
    Erst binden, dann abgleichen – in dieser Reihenfolge und keiner anderen.

    Der Zwischenspeicher im Browser überlebt Abmelden, Neuladen und das
    Schliessen des Fensters. Er trug bis hierher nirgends, WEM er gehört, und
    das hatte eine Folge, die niemandem eine Fehlermeldung erzeugt: Wer sich
    im selben Browser einen zweiten Betrieb einrichtet, sah den Bestand des
    ersten – Firmenangaben, Kunden, Baustellen, Katalog.

    Der Grund ist der Zählstand. Der Abgleich holt, was NEUER ist als das, was
    das Gerät schon hat. Stand dort der Zählstand des alten Betriebs, lag er
    über allem, was der neue je hatte, und es kam nichts nach. Was blieb, war
    das Bild des fremden Betriebs.

    bindeAn() leert den Zwischenspeicher, sobald Mandant oder Person
    wechseln – vor dem ersten Abgleich und vor dem ersten Bildschirm.

    Die Abhängigkeit steht bewusst auf den beiden Kennungen und nicht auf dem
    Sitzungsobjekt: das ist bei jedem Rendern ein neues und würde den Motor
    ohne Grund neu starten.
  */
  /*
    Abmelden, richtig.

    Das stand vorher nur auf dem Statusbildschirm. Der Knopf in der
    Seitenleiste – der, den ein Betrieb tatsächlich benutzt – setzte bloss die
    Anzeige zurück und schickte auf die Startseite. Das Sitzungscookie blieb
    stehen; beim nächsten Laden war man wieder drin.

    Auf einem Bürolaptop, den sich zwei teilen, heisst das: der eine meldet
    sich ab, der andere lädt die Seite und ist im Konto des ersten. Und im
    Browser blieb der Zwischenspeicher des alten Betriebs liegen – das war der
    Weg, auf dem ein neu eingerichteter Betrieb die Daten des alten sah.

    Deshalb: einer Weg, drei Dinge, in dieser Reihenfolge – Sitzung beim
    Server beenden, Zwischenspeicher leeren, Abgleich anhalten.
  */
  /**
   * Eine vollständige Anmeldung ist durch.
   *
   * ──────────────────────────────────────────────────────────────────────────
   * Warum das eine eigene Stelle ist und nicht dreimal `setSession`
   * ──────────────────────────────────────────────────────────────────────────
   *
   * Hier endet jeder Weg herein: die gewöhnliche Anmeldung mit Passwort und
   * zweitem Faktor, die Registrierung und die Rückkehr aus der Kasse. An
   * genau dieser Stelle – und nur hier – ist der Nachweis frisch erbracht.
   *
   * Deshalb wird hier die Sperrspur dieses Geräts gelöscht. Sie überlebte
   * bisher Abmeldung und Neuanmeldung, und der gemeldete Produktionsfehler
   * war die Folge: Nach erfolgreicher Anmeldung hing sofort wieder der
   * Vorhang, in jedem Tab, ohne Weg hinaus.
   *
   * Zuerst gelöscht, DANN die Sitzung gesetzt – nicht umgekehrt. Der
   * Sperrbildschirm liest die Merker, sobald er gezeichnet wird, und
   * gezeichnet wird er erst mit der Sitzung. Andersherum läge ein Wettlauf
   * dazwischen, den man nur manchmal verliert; das ist die schlimmere Sorte
   * Fehler.
   *
   * Die Auskunft des Servers wird ebenfalls zurückgesetzt: Sie beschrieb die
   * vorige Sitzung. Bliebe sie stehen, entschiede die dritte Frage im
   * Sperrbildschirm nach einer Zahl, die nicht mehr gilt.
   */
  const anmeldungFertig = useCallback(
    async (s: SessionUser): Promise<void> => {
      try {
        await sperrspurLoeschen()
      } catch {
        /*
          Gesperrter oder fehlender Speicher: Dann gibt es auch keine
          Sperrspur, die uns aussperren könnte. Die Anmeldung daran scheitern
          zu lassen wäre das falsche Verhältnis.
        */
      }
      setInaktivMs(undefined)
      setSperrzustand(null)
      setSession(s)
      geheZu('app')
    },
    [geheZu]
  )

  const abmelden = useCallback(async (): Promise<void> => {
    try {
      await api.logout()
    } catch (err) {
      /*
        Kein Netz? Trotzdem abmelden. Die Sitzung serverseitig stehen zu
        lassen ist das kleinere Übel gegenüber einem Gerät, auf dem der
        Bestand des Betriebs liegen bleibt, weil der Knopf nichts tat.
      */
      if (!(err instanceof ApiError) || err.kind !== 'network') throw err
    }
    clearCookieJar()
    await clearOnLogout()
    stop()
    /*
      Die Meldungen der beendeten Sitzung gehen mit ihr.

      Ohne diese Zeile stünde eine Verwaltungsmeldung des Betreibers noch auf
      dem Anmeldebildschirm – und damit vor den Augen des Nächsten, der sich
      an diesem Gerät anmeldet.
    */
    clearToasts()
    setSession(null)
    /*
      Der Sperrentscheid gehörte zur eben beendeten Sitzung.

      Bliebe er auf «offen» stehen, liefe der Abgleich der NÄCHSTEN Anmeldung
      im selben Fenster los, bevor der Sperrbildschirm überhaupt nachgesehen
      hat. Ein Neuladen setzt ihn ohnehin zurück; dieser Weg hier nicht.
    */
    setSperrzustand(null)
    setInaktivMs(undefined)
    geheZu('start')
  }, [geheZu])

  /*
    Der Abgleich startet erst, wenn feststeht, dass NICHT gesperrt wird.

    ────────────────────────────────────────────────────────────────────────────
    Warum diese Reihenfolge notwendig ist
    ────────────────────────────────────────────────────────────────────────────

    Seit `zaehltAlsBenutzung()` am Server zählt echte Benutzung – und der
    Abgleich im Hintergrund ist echte Benutzung. `/sync/bootstrap` frischt
    `last_seen_at` auf, und zwar zu Recht.

    Nur: Er lief bisher los, sobald eine Sitzung da war – auch hinter dem
    Sperrbildschirm. Damit wäre die eben geschlossene Lücke über den Umweg
    wieder offen gewesen. Der Ablauf im Einzelnen:

      1. Nach 90 Minuten Ruhe wird geladen, der Vorhang fällt.
      2. Der Abgleich läuft trotzdem weiter und setzt die Uhr auf jetzt.
      3. Websitedaten löschen, neu laden.
      4. `/me` meldet jetzt zwei Sekunden statt 90 Minuten – offen.

    Deshalb wartet der Start auf den Entscheid, und der Aufräumer stoppt den
    Abgleich, sobald der Vorhang fällt. Hinter dem Vorhang geht keine Anfrage
    mehr hinaus ausser der einen, mit der jemand sein Passwort einlöst – und
    die frischt erst auf, wenn das Passwort gestimmt hat.

    `null` heisst «noch nicht entschieden» und wartet ebenfalls. Fail-closed:
    Im Zweifel schweigt die Anwendung, statt sich selbst frisch zu halten.

    ────────────────────────────────────────────────────────────────────────────
    Das Zuordnen wartet NICHT – und warum das kein Widerspruch ist
    ────────────────────────────────────────────────────────────────────────────

    Gewartet hat anfangs beides: Abgleich und `bindeAn()`. Das war ein Fehler,
    und er hat einen fremden Firmennamen auf den Sperrbildschirm gebracht.

    `bindeAn()` schickt nichts hinaus. Es liest und löscht ausschliesslich im
    Gerätespeicher: Gehört der Zwischenspeicher einem anderen Betrieb oder
    einer anderen Person, wird er geleert, und erst dann steht die neue
    Zuordnung da. Keine Anfrage, keine Auffrischung der Sitzung – der Grund
    für das Warten trifft darauf also gar nicht zu.

    Gewartet hat es trotzdem, weil die Prüfung ganz oben im Effekt stand und
    beides zugleich aufhielt. Damit blieb hinter dem Vorhang der
    Zwischenspeicher des Vorgängers liegen, und der gebrandete
    Sperrbildschirm las ihn.

    Die Prüfung steht deshalb jetzt eine Zeile tiefer: zuordnen, sobald eine
    Sitzung da ist – abgleichen erst, wenn feststeht, dass nicht gesperrt
    wird. In dieser Reihenfolge, im selben Effekt, denn umgekehrt liefe der
    Abgleich gegen den Zählstand des alten Betriebs.

    Mehrfaches Zuordnen schadet nicht: Stimmen Betrieb und Person schon,
    räumt `bindeAn()` nichts und meldet `gewechselt: false`.
  */
  useEffect(() => {
    if (!session) return
    let abgebrochen = false
    void (async () => {
      const { gewechselt, fremdeAusgaenge } = await bindeAn(session.tenantId, session.userId)
      if (abgebrochen) return
      if (gewechselt && fremdeAusgaenge > 0) {
        /*
          Nicht verwerfen und nicht senden. Die Einträge gehören einem anderen
          Betrieb; sie gehen los, sobald der sich anmeldet. Gesagt werden muss
          es trotzdem – ein Stapel, der stillschweigend nie losgeht, ist am
          Ende derselbe Verlust wie ein gelöschter.
        */
        notify.warn(
          `${fremdeAusgaenge} nicht gesendete Einträge gehören zu einem anderen Betrieb. ` +
            'Sie bleiben liegen, bis du dich dort wieder anmeldest.'
        )
      }
      if (sperrzustand !== false) return
      await start()
    })()
    return () => {
      abgebrochen = true
      stop()
    }
  }, [session?.tenantId, session?.userId, sperrzustand])

  if (loading) {
    return (
      <div className="rp-public">
        <div className="rp-anmeldung">
          <p className="muted">Einen Moment …</p>
        </div>
      </div>
    )
  }

  /*
    Die Rechtsseiten stehen VOR der Anmeldeprüfung.

    Sie sind für jeden zugänglich, angemeldet oder nicht – auch für den
    Treuhänder eines Betriebs, der wissen will, was mit den Daten geschieht,
    und der hier keinen Zugang hat.
  */
  if (ort === 'recht') {
    return <Recht id={rechtId} angemeldet={Boolean(session)} aufRecht={aufRecht} {...glasKopf} />
  }

  if (ort === 'start') {
    return (
      <Landing
        angemeldet={Boolean(session)}
        aufLogin={() => geheZu('login')}
        aufApp={() => geheZu(session ? 'app' : 'login')}
        aufPreise={() => geheZu('preise')}
        aufFunktionen={() => geheZu('funktionen')}
        aufBranchen={() => geheZu('branchen')}
        aufPfad={aufPfad}
        aufBestellen={bestellen}
        aufRecht={aufRecht}
      />
    )
  }

  if (ort === 'preise') {
    return (
      <Preise
        angemeldet={Boolean(session)}
        {...glasKopf}
        aufPfad={aufPfad}
        aufBestellen={bestellen}
      />
    )
  }

  /*
    Die Fragenseite steht bewusst als eigener Ort und nicht als Anker auf der
    Preisseite. Ein Betrieb, der eine Frage weitergibt, gibt einen Verweis
    weiter – und der soll «/faq» heissen und nicht «/preise#fragen».
  */
  if (ort === 'faq') {
    return <Faq angemeldet={Boolean(session)} aufPreise={() => geheZu('preise')} {...glasKopf} />
  }

  if (ort === 'kontakt') {
    return <Kontakt angemeldet={Boolean(session)} {...glasKopf} />
  }

  if (ort === 'marke') {
    return <Markenseite angemeldet={Boolean(session)} {...glasKopf} />
  }

  if (ort === 'funktionen') {
    return (
      <Funktionen
        angemeldet={Boolean(session)}
        {...glasKopf}
        aufPreise={() => geheZu('preise')}
        aufPfad={aufPfad}
      />
    )
  }

  if (ort === 'branchen') {
    return (
      <Branchen
        angemeldet={Boolean(session)}
        {...glasKopf}
        aufPreise={() => geheZu('preise')}
        aufPfad={aufPfad}
      />
    )
  }

  /*
    Die Bestellung steht vor der Anmeldung.

    Wer hier ist, hat noch keinen Zugang – ihn auf das Anmeldeformular zu
    schicken, weil keine Sitzung besteht, wäre genau der Kreis, aus dem er
    nicht herausfindet.
  */
  /*
    Ein Link aus einer E-Mail – vor allem anderen.

    Auch für jemanden, der zufällig noch angemeldet ist: wer auf «Passwort
    zurücksetzen» geklickt hat, will das tun und nicht auf seinem Dashboard
    landen.
  */
  if (ort === 'zugang') {
    const pfad = window.location.pathname
    const token = new URLSearchParams(window.location.search).get('token') ?? ''
    return (
      <Zugang
        art={pfad.startsWith('/einladung') ? 'einladung' : 'passwort'}
        token={token}
        aufLogin={() => geheZu('login')}
      />
    )
  }

  /*
    Die Einladung in eine Kita-Gemeinschaft.

    Dieselbe Begründung wie bei den beiden Linkpfaden darüber: Wer hier
    ankommt, kommt aus einer Mail und ist meist nicht angemeldet – oft hat er
    noch gar keinen Zugang. Die Seite legt nichts an; sie zeigt, wer einlädt,
    und bietet die zwei Wege an.
  */
  if (ort === 'gemeinschaftseinladung') {
    const token = new URLSearchParams(window.location.search).get('einladung') ?? ''
    return (
      <Gemeinschaftseinladung
        token={token}
        aufBestellen={(t) => geheZu('bestellen', `?einladung=${encodeURIComponent(t)}`)}
        aufLogin={() => geheZu('login')}
        aufStart={() => geheZu('start')}
      />
    )
  }

  if (ort === 'anmeldelink') {
    const token = new URLSearchParams(window.location.search).get('token') ?? ''
    return <Anmeldelink token={token} aufLogin={() => geheZu('login')} />
  }

  /*
    Die Rückkehr aus dem Checkout – vor der Abfrage auf `session`.

    Wer hier ankommt, hat keine Sitzung; er bekommt sie von dieser Seite,
    sobald der Webhook den Betrieb angelegt hat.
  */
  if (ort === 'willkommen') {
    return (
      <Checkoutrueckkehr
        aufAngemeldet={(s) => void anmeldungFertig(s)}
        aufLogin={() => geheZu('login')}
        aufStart={() => geheZu('start')}
      />
    )
  }

  /*
    Die eingebettete Kasse.

    Sie bekommt den Schlüssel aus dem Zustand dieser Anwendung und nicht aus
    der Adresszeile. Nach einem harten Neuladen ist er weg – dann sagt die
    Seite das und schickt zurück ins Formular, statt eine leere Fläche zu
    zeigen. Belastet wurde in dem Fall nichts: Die Kasse war nie offen.
  */
  if (ort === 'zahlung') {
    return (
      <Zahlung
        clientSecret={kasse?.clientSecret ?? null}
        publicKey={kasse?.publicKey ?? ''}
        sandbox={kasse?.sandbox ?? false}
        betrieb={kasse?.betrieb ?? ''}
        aufZurueck={() => geheZu('bestellen')}
        aufStart={() => geheZu('start')}
      />
    )
  }

  if (ort === 'bestellen' && !session) {
    return (
      <Bestellung
        paketWunsch={paketWunsch}
        /*
          Der Schlüssel aus der Einladungsmail, falls dieser Weg von dort
          kommt. Er steht in der Adresse und wandert unverändert in die
          Bestellung – das Formular selbst ist dasselbe wie für jeden anderen
          Betrieb.
        */
        einladung={
          new URLSearchParams(window.location.search).get('einladung') ?? undefined
        }
        aufZahlung={(k) => {
          setKasse(k)
          geheZu('zahlung')
        }}
        aufAngemeldet={(s, paket) => {
          void anmeldungFertig(s)
          /*
            Und weiter zur Zahlung.

            Der neue Inhaber ist in diesem Moment angemeldet – die Sitzung kam
            mit der Antwort der Registrierung. Ihn auf die Uebersicht zu
            schicken hiesse, den Schritt zu ueberspringen, fuer den er eben ein
            Paket ausgewaehlt hat.
          */
          setStartPaket(paket)
        }}
        aufStart={() => geheZu('start')}
        aufLogin={() => geheZu('login')}
      />
    )
  }

  /*
    Wer angemeldet ist und /login aufruft, will nicht das Formular sehen,
    sondern seine Arbeit. Und wer nicht angemeldet ist und /app aufruft,
    bekommt die Anmeldung – nicht eine leere Oberfläche mit Fehlermeldungen
    aus jeder Abfrage.
  */
  if (!session) {
    /*
      Und die Adresse geht mit.

      Bisher blieb `/app` in der Zeile stehen, während das Anmeldeformular
      darunter stand. Wer neu lud, landete wieder auf derselben Adresse, und
      wer den Verweis weitergab, schickte jemanden auf eine Anwendung, die er
      nicht öffnen kann.
    */
    if (ort === 'app') geheZu('login')
    return (
      <Login
        aufAngemeldet={(s) => void anmeldungFertig(s)}
        aufStart={() => geheZu('start')}
      />
    )
  }

  if (ort === 'login') {
    geheZu('app')
  }

  /*
    Der Sperrbildschirm liegt um den angemeldeten Bereich und nicht um die
    ganze Anwendung: die Startseite und die Preisseite haben nichts zu
    verbergen, und ein Vorhang über einer öffentlichen Seite wäre nur im Weg.
  */
  return (
    <Sperre
      session={session}
      inaktivMs={inaktivMs}
      aufZustand={setSperrzustand}
      aufAbmelden={abmelden}
      aufStartseite={() => geheZu('start')}
      kinder={
        <Angemeldet
          session={session}
          betreiber={istBetreiber}
          plattformbetrieb={istPlattform}
          startPaket={startPaket}
          aufStartPaketErledigt={() => setStartPaket(null)}
          onLogout={abmelden}
          /*
        Die Sitzung ändert sich im Betrieb – etwa in dem Moment, in dem jemand
        den zweiten Faktor einrichtet. Ohne diesen Weg zurück blieben alle
        Knöpfe gesperrt, bis er die Seite neu lädt, und er würde denken, es
        habe nicht geklappt.
      */
          onSession={(s) => setSession(s)}
          aufStartseite={() => geheZu('start')}
        />
      }
    />
  )
}

function Shell({
  children,
  bar,
  nav
}: {
  children: React.ReactNode
  bar?: boolean
  nav?: React.ReactNode
}): JSX.Element {
  return (
    <div className="shell">
      <header>
        {/*
          Auch die Monteuransicht trägt die Marke und nicht ein fett gesetztes
          Wort. Dass sie auf dem Handy dieselbe ist wie im Büro, ist der Punkt:
          es ist eine Software, und wer sie von beiden Seiten benutzt, soll
          das nicht erst glauben müssen.
        */}
        <Marke groesse={26} />
        <span className="stage">Feldtest</span>
      </header>
      {bar && <ConnectionBar />}
      {nav}
      <main>{children}</main>
      <Toasts />
    </div>
  )
}

function Angemeldet({
  session,
  betreiber,
  plattformbetrieb,
  startPaket,
  aufStartPaketErledigt,
  onLogout,
  onSession,
  aufStartseite
}: {
  session: SessionUser
  /** Betreibt dieses Konto Raporto selbst? Nur dann gibt es den Bereich «Betreiber». */
  betreiber: boolean
  /**
   * Sitzt die Sitzung auf dem Plattformbetrieb? Dann gibt es «Mein Raporto»
   * nicht – der Bereich gehört einem Kunden, und dieser Betrieb ist keiner.
   */
  plattformbetrieb: boolean
  /** Das eben bei der Bestellung gewaehlte Paket – sonst null. */
  startPaket: string | null
  aufStartPaketErledigt: () => void
  onLogout: () => Promise<void>
  onSession: (s: SessionUser) => void
  aufStartseite: () => void
}): JSX.Element {
  const superuser = session.role === 'superuser'
  /*
    Der Superuser landet auf dem Dashboard.

    In Schritt 1 war es die Express-Rechnung – richtig gedacht, aber einen
    Schritt zu weit: wer morgens hereinkommt, will zuerst wissen, was
    liegengeblieben ist, und erst dann schreiben. Die Express-Rechnung bleibt
    trotzdem einen Klick entfernt und steht als erster Schnellknopf oben auf
    dem Dashboard – der häufigste Handgriff des Tages darf keinen Umweg
    kosten.

    Der Monteur startet unverändert bei seiner Erfassung.
  */
  const [tab, setTab] = useState<Tab>(
    /*
      Wer gerade bestellt hat, landet nicht auf der Uebersicht, sondern dort,
      wo er sein Abo abschliesst. Die Uebersicht kommt danach – und zwar von
      selbst, sobald er «Mein Raporto» verlaesst.
    */
    startPaket !== null && superuser && !plattformbetrieb
      ? 'meinraporto'
      : superuser
        ? 'dashboard'
        : 'start'
  )

  /*
    ════════════════════════════════════════════════════════════════════════
    Der Kontextwechsel räumt die Meldungen weg
    ════════════════════════════════════════════════════════════════════════

    Die Meldungsliste in `ui/Toasts.tsx` liegt auf Modulebene – sie überlebt
    jeden Bildschirmwechsel, und Fehlermeldungen bleiben stehen, bis jemand
    sie wegklickt (`timeoutMs: 0`, mit Absicht: ein Fehler, der von selbst
    verschwindet, ist einer, den niemand gelesen hat).

    Beides zusammen hatte eine Folge, die niemand gebaut hat: Ein
    fehlgeschlagener Löschversuch in der Control Plane hinterliess
    «SOFORTLOESCHUNG_AKTIV» und «PRG-03» – und diese Sätze standen danach im
    Büro des Betriebs. Auf dem Telefon lagen sie über den Rechnungen und den
    Schaltflächen. Interne Fehlercodes gehören einem Administrator; in einer
    Kundenansicht sind sie bestenfalls Rauschen.

    Geräumt wird am WECHSEL und nicht beim Betreten eines Bildschirms: Eine
    Meldung, die zum gerade angesehenen Vorgang gehört, soll stehen bleiben.
    Drei Dinge bilden den Kontext – der Betrieb, das Konto und der Rahmen
    (Control Plane oder Büro). Ändert sich eines davon, gehört die alte
    Meldung nicht mehr hierher.

    `clearToasts()` gab es schon; gerufen hat es bis zum 27. Oktober 2026
    ausschliesslich der Test.
  */
  const meldungskontext = `${session.tenantId}|${session.userId}|${tab === 'betreiber' ? 'control-plane' : 'buero'}`
  useEffect(() => {
    clearToasts()
  }, [meldungskontext])

  /*
    Einen Bereich zeigen – und zwar von oben.

    `setTab` allein wechselt den Inhalt und lässt den Rollstand stehen. Das
    fällt auf dem Schreibtisch kaum auf und am Telefon sofort: Wer in einer
    langen Liste nach unten gerollt ist und dann eine Kachel oder einen
    Menüpunkt antippt, landet im neuen Bereich mitten im Inhalt – der Kopf
    mit Titel und Knöpfen liegt oberhalb des sichtbaren Ausschnitts.
    Gemessen am Telefon: Der Knopf «← Übersicht» stand zwar im Baum, war
    aber von der Kopfleiste verdeckt und antwortete nicht auf einen Tipper.

    Gerollt wird der Kasten, der wirklich rollt – im Büro ist das nicht das
    Fenster, sondern `.office-main`. Gibt es ihn nicht (der Monteur hat eine
    andere Hülle), rollt das Fenster.
  */
  const zeigeTab = useCallback((ziel: Tab): void => {
    setTab(ziel)
    const kasten = document.querySelector('.office-main')
    if (kasten) kasten.scrollTop = 0
    else window.scrollTo(0, 0)
  }, [])

  /*
    Ein Sprung in eine Liste – mit der Herkunft im Gepäck.

    Die Herkunft ist nicht Buchführung, sondern der Rückweg: Die Zielliste
    zeigt «← Übersicht» nur, wenn man wirklich von der Übersicht kam. Vom
    Knopf «Mehrere erfassen» in den Zahlungen aus wäre derselbe Knopf eine
    falsche Zusage.
  */
  const springe = useCallback(
    (ziel: Sprung): void => {
      setSprungListe({ ...ziel, herkunft: tab })
      zeigeTab(ziel.bereich as Tab)
    },
    [tab, zeigeTab]
  )
  /*
    Das bei der Bestellung gewaehlte Paket – nur fuer den ersten Bildschirm
    nach der Registrierung. Danach ist es verbraucht.
  */
  /** Ein aus der Arbeitsliste angesprungener Beleg. */
  const [sprungBeleg, setSprungBeleg] = useState<string | null>(null)
  /*
    Eine angesprungene Offerte.

    Getrennt von `sprungBeleg`, weil Offerten und Rechnungen in getrennten
    Listen stehen. Vorher führte «Zur Offerte» nach dem Abschliessen in die
    Rechnungsliste und dort ins Leere – der Beleg war eine Offerte und stand
    in der anderen Liste.
  */
  const [sprungOfferte, setSprungOfferte] = useState<string | null>(null)
  /*
    Eine vom Dashboard mitgegebene Einstellung für die Zielliste.

    Dieselbe Bauart wie `sprungBeleg`: Die App hält sie, die Liste nimmt sie
    einmal entgegen und meldet zurück, dass sie verbraucht ist. Ohne das
    Zurückmelden stellte sich der Filter bei jedem Neuzeichnen wieder her,
    und wer danach einen anderen Filter anklickte, bekäme den alten zurück.
  */
  const [sprungListe, setSprungListe] = useState<Sprung | null>(null)
  /*
    Die Zahl an der Glocke.

    Sie stand immer auf null – nicht, weil nichts offen war, sondern weil
    niemand den Wert übergab: `OfficeShell` nahm `hinweise = 0` entgegen, und
    kein Aufrufer setzte ihn. Eine Glocke, die dauerhaft «Keine Hinweise»
    meldet, ist schlimmer als keine: man hört auf hinzusehen.

    Die Zahl kommt von der Übersicht, sobald sie geladen hat. Ein zweiter
    Aufruf nur für die Glocke wäre Verkehr für eine Ziffer.
  */
  const [hinweiszahl, setHinweiszahl] = useState(0)
  /*
    Fuehrt dieser Betrieb Betreuungsverhaeltnisse?

    Eine Auskunft fuer die Navigation, einmal geholt. Sie schaltet nichts
    frei: Ein `true` von hier bringt keinen Parkettleger an die
    Betreuungsrechnungen, denn jede Route prueft die Branche selbst.
  */
  const [betreuung, setBetreuung] = useState(false)
  useEffect(() => {
    /*
      Einmal je Sitzung. Ein Monteur fragt nicht: Er sieht den Bereich
      ohnehin nicht, und die Auskunft verlangt `customer.read`.
    */
    if (!superuser) return
    let abgebrochen = false
    void (async () => {
      try {
        const { verfuegbar } = await api.betreuungVerfuegbar()
        if (!abgebrochen) setBetreuung(verfuegbar)
      } catch {
        /* Keine Auskunft, kein Eintrag. Der Bereich fehlt dann still – er
           waere ohnehin leer, und eine Fehlermeldung im Menue hilft keinem. */
      }
    })()
    return () => {
      abgebrochen = true
    }
  }, [superuser])
  /*
    Jeder Griff ins Menü zählt einmal hoch.

    Ein Bereichswechsel allein genügt nicht: Wer «Rechnungen» anklickt,
    während ein Beleg offen ist, ist bereits im Bereich «rechnungen» – der
    Zustand ändert sich nicht, und der Bildschirm blieb im Beleg stehen.
    Diese Zahl ändert sich trotzdem, und die Belegliste schliesst daraufhin
    ihr Detail.
  */
  const [menuSignal, setMenuSignal] = useState(0)
  const ausMenue = useCallback(
    (id: string): void => {
      /* Ein alter Sprungwunsch darf nach einem Menüklick nicht nachträglich zuschlagen. */
      setSprungBeleg(null)
      setSprungListe(null)
      setMenuSignal((n) => n + 1)
      zeigeTab(id as Tab)
    },
    [zeigeTab]
  )
  /**
   * Die im Rapport gewählten Zeilen auf dem Weg zum Belegbildschirm.
   *
   * Bewusst ein Zustand hier oben und keine Zwischenablage im Belegbildschirm:
   * der Wechsel Rapport → Rechnung ist ein einziger Handgriff, und der Auftrag
   * darf keinen Reiterwechsel überleben, den der Benutzer zwischendurch macht.
   */
  const [auftrag, setAuftrag] = useState<{
    customerId: string
    projectId: string
    lineIds: string[]
  } | null>(null)
  /** Eine aus einer Liste angesprungene Baustelle. */
  const [sprungProjekt, setSprungProjekt] = useState<string | null>(null)
  /**
   * Der Schnellstart aus Kunde oder Baustelle.
   *
   * Er lebt hier oben und nicht im Express-Bildschirm, weil er einen
   * Bereichswechsel überdauern muss: geklickt wird in der Kundenliste,
   * ausgefüllt wird im Express-Formular.
   */
  const [vorgabe, setVorgabe] = useState<Vorgabe | null>(null)

  function schnellstart(art: 'rechnung' | 'offerte', v: Vorgabe): void {
    setVorgabe(v)
    setTab(art === 'offerte' ? 'expressofferte' : 'express')
  }
  const visible = TABS.filter(
    (t) =>
      (!t.superuserOnly || superuser) &&
      (!t.workerOnly || !superuser) &&
      /*
        «Mein Raporto» gibt es auf dem Plattformbetrieb nicht.

        Der Betreiber ist dort superuser und sah den Reiter deshalb wie jeder
        Inhaber – mit Paketkarten und einem Knopf «Weiter zur Zahlung». Der
        Server weist das inzwischen ab; hier wird der Reiter gar nicht erst
        gezeichnet, damit niemand gegen eine Tür läuft, die richtigerweise zu
        ist.
      */
      !(t.id === 'meinraporto' && plattformbetrieb)
  )

  const { firma, logoKey } = useEinstellungen()

  /*
    Das Warnband.

    Zwei Fälle, und der zweite ist der unangenehmere:

      Der zweite Faktor fehlt – dann soll die betroffene Person wissen, was
      das für sie bedeutet, bevor sie auf einen Knopf drückt, der nicht geht.

      Der Betrieb hat die Pflicht abgeschaltet (RAPORTO_REQUIRE_2FA=false).
      Dann ist nichts gesperrt, und genau deshalb muss es dastehen: eine
      stille Abschaltung ist schlimmer als gar kein Schutz, weil niemand mehr
      daran denkt.

    Beide Bänder gelten jetzt für jede Rolle – der zweite Faktor hängt an der
    Person. Was sich nach Rolle unterscheidet, ist einzig der Satz über die
    Folgen, und der muss sich unterscheiden: beim Monteur ist nichts gesperrt,
    weil er Zahlungen, Exporte und Rechnungsabschluss ohnehin nicht darf. Ihm
    zu schreiben, diese Dinge seien gesperrt, wäre eine Drohung mit etwas, das
    ihn nie betrifft – und beim nächsten Hinweis liest er gar nicht mehr hin.
  */
  const band =
    !session.twoFactorEnabled && session.twoFactorRequired ? (
      <div className="o-band" id="band-2fa-fehlt">
        <span>
          <strong>Zwei-Faktor-Authentifizierung fehlt.</strong>{' '}
          {superuser
            ? 'Der Rechnungsabschluss, der Versand, Zahlungen, Exporte und die Bankverbindung sind gesperrt, bis sie eingerichtet ist. Stammdaten, Texte und dein Passwort kannst du auch ohne sie ändern.'
            : 'Dein Zugang hängt bis dahin allein am Passwort. Die Einrichtung dauert zwei Minuten und braucht nur dein Telefon; am Erfassen von Rapporten ändert sich nichts.'}
        </span>
        <button className="o-btn" id="btn-band-sicherheit" onClick={() => setTab('sicherheit')}>
          Jetzt einrichten
        </button>
      </div>
    ) : !session.twoFactorEnabled ? (
      <div className="o-band warnung" id="band-2fa-aus">
        <span>
          <strong>Der zweite Faktor ist für diese Installation abgeschaltet.</strong> Für den
          Feldtest im eigenen Netz ist das vertretbar. Vor der ersten echten Kundenrechnung gehört
          er eingeschaltet.
        </span>
        <button className="o-btn" id="btn-band-sicherheit" onClick={() => setTab('sicherheit')}>
          Zur Sicherheit
        </button>
      </div>
    ) : null

  const inhalt = (
    <>
      {band}
      {tab === 'start' && <WorkerHome session={session} />}
      {tab === 'dashboard' && (
        <Dashboard
          session={session}
          aufBeleg={(id) => {
            setSprungBeleg(id)
            setTab('rechnungen')
          }}
          aufBereich={(id) => zeigeTab(id as Tab)}
          aufListe={springe}
          aufHinweiszahl={setHinweiszahl}
        />
      )}
      {/*
        Der Weg vom fertigen Beleg zum Beleg.

        `aufBeleg` fehlte an genau diesen zwei Stellen. `ExpressInvoice` hat
        den Knopf «Zur Rechnung / Zur Offerte» seit langem gebaut – gezeigt
        wird er nur, wenn es auch einen Ort gibt, an den er führt. Ohne die
        Rückgabe endete der Bildschirm nach dem Abschliessen bei «PDF öffnen»,
        und der Weg zum Versand führte über Menü und Liste zurück zu genau
        dem Beleg, den man eben geschrieben hatte.
      */}
      {tab === 'express' && (
        <ExpressInvoice
          session={session}
          art="rechnung"
          vorgabe={vorgabe}
          onVorgabeUebernommen={() => setVorgabe(null)}
          aufBeleg={(id) => {
            setSprungBeleg(id)
            setTab('rechnungen')
          }}
          aufEinstellungen={() => setTab('einstellungen')}
        />
      )}
      {tab === 'expressofferte' && (
        <ExpressInvoice
          session={session}
          art="offerte"
          vorgabe={vorgabe}
          onVorgabeUebernommen={() => setVorgabe(null)}
          aufBeleg={(id) => {
            /*
              Die Offerte führt in die Offerte.

              Hier stand `setTab('offerten')` ohne Kennung, mit der Begründung,
              es gebe keinen Offertbildschirm je Offerte. Den gibt es: die
              Offertenliste öffnet `BelegDetail`. Wer eine Offerte gerade
              abgeschlossen hat, landete trotzdem in einer Liste und musste die
              Nummer suchen – auf dem Telefon der häufigste Abbruchpunkt.

              `sprungOfferte` und nicht `sprungBeleg`: das eine wertet die
              Offertenliste aus, das andere die Rechnungsliste.
            */
            setSprungOfferte(id)
            setTab('offerten')
          }}
          aufEinstellungen={() => setTab('einstellungen')}
        />
      )}
      {tab === 'offerten' && (
        <Quotes
          session={session}
          aufBeleg={(id) => {
            setSprungBeleg(id)
            setTab('rechnungen')
          }}
          oeffneBeleg={sprungOfferte}
          onBelegGeoeffnet={() => setSprungOfferte(null)}
          sprung={sprungListe}
          onSprungUebernommen={() => setSprungListe(null)}
          aufUebersicht={() => zeigeTab('dashboard')}
          aufBereich={(id) => zeigeTab(id as Tab)}
        />
      )}
      {tab === 'offen' && (
        <OpenWork
          session={session}
          onOeffnen={(ziel) => {
            if (ziel.typ === 'beleg') {
              setSprungBeleg(ziel.id)
              setTab('rechnungen')
            } else {
              setTab('rapporte')
            }
          }}
        />
      )}
      {tab === 'rapporte' && (
        <Rapports
          session={session}
          onVerrechnen={(auswahl) => {
            setAuftrag(auswahl)
            setTab('rechnungen')
          }}
        />
      )}
      {tab === 'rechnungen' && (
        <Documents
          session={session}
          auftrag={auftrag}
          onAuftragErledigt={() => setAuftrag(null)}
          oeffneBeleg={sprungBeleg}
          onBelegGeoeffnet={() => setSprungBeleg(null)}
          sprung={sprungListe}
          onSprungUebernommen={() => setSprungListe(null)}
          aufUebersicht={() => zeigeTab('dashboard')}
          menuSignal={menuSignal}
          aufBereich={(id) => zeigeTab(id as Tab)}
        />
      )}
      {/*
        Zwei Fassungen, ein Bereich.

        Das Büro bekommt die breite Liste mit Zahlen, der Monteur behält die
        schmale Auswahlliste. Sie ist für eine Hand mit Handschuh gebaut, und
        eine Tabelle mit neun Spalten wäre dort unbrauchbar – Kunden sieht er
        ohnehin nicht, Projekte und Leistungen schon.
      */}
      {tab === 'kunden' &&
        (superuser ? (
          <OfficeCustomers
            session={session}
            aufBeleg={(id) => {
              setSprungBeleg(id)
              setTab('rechnungen')
            }}
            aufProjekt={(id) => {
              setSprungProjekt(id)
              setTab('projekte')
            }}
            aufSchnellstart={schnellstart}
          />
        ) : (
          <Customers session={session} />
        ))}
      {tab === 'projekte' &&
        (superuser ? (
          <OfficeProjects
            session={session}
            aufBeleg={(id) => {
              setSprungBeleg(id)
              setTab('rechnungen')
            }}
            aufRapporte={() => setTab('rapporte')}
            aufSchnellstart={schnellstart}
            oeffneProjekt={sprungProjekt}
            onProjektGeoeffnet={() => setSprungProjekt(null)}
          />
        ) : (
          <Projects session={session} />
        ))}
      {tab === 'betreuung' && superuser && <OfficeBetreuung session={session} />}
      {/*
        Der Dachbereich. Dieselbe Begründung wie bei «Betreuung»: Die Zeile
        prüft die Rolle noch einmal, weil `tab` auch aus einem Link oder dem
        Verlauf kommen kann. Der Schutz selbst steht am Server – ein Betrieb
        ohne Gemeinschaft bekommt dort 404.
      */}
      {tab === 'gemeinschaft' && superuser && <OfficeGemeinschaft session={session} />}
      {tab === 'katalog' &&
        (superuser ? <OfficeCatalog session={session} /> : <Catalog session={session} />)}
      {tab === 'einstellungen' && <Settings session={session} aufSession={onSession} />}
      {/*
        «Team» ist derselbe Bildschirm wie die Einstellungen, nur mit einem
        anderen Startabschnitt. Kein zweiter Datenpfad, keine zweite Stelle,
        die Firmenangaben lädt – nur ein auffindbarer Weg dorthin.
      */}
      {tab === 'team' && (
        <Settings session={session} aufSession={onSession} startAbschnitt="team" />
      )}
      {tab === 'sicherheit' && <Security session={session} aufSession={onSession} />}
      {/*
        Der Reiter ist für den Inhaber gedacht, und die Zeile hier prüft es
        noch einmal. Nicht aus Misstrauen gegen `superuserOnly` oben, sondern
        weil `tab` aus dem Zustand kommt und ein späterer Sprung – aus einem
        Link, aus dem Verlauf, aus einer Verknüpfung – an der Reiterliste
        vorbeiführen kann. Der eigentliche Schutz steht ohnehin am Server.
      */}
      {tab === 'meinraporto' && session.role === 'superuser' && !plattformbetrieb && (
        <MeinRaporto startPaket={startPaket} aufStartPaketErledigt={aufStartPaketErledigt} />
      )}
      {tab === 'status' && <Status session={session} onLogout={onLogout} />}

      {/*
        Bereiche, die in der Seitenleiste stehen und noch nicht gebaut sind.

        Ein Eintrag, der ins Leere klickt, ist schlimmer als keiner: der
        Betrieb sucht dann den Fehler bei sich. Deshalb steht hier, was
        kommt und was heute stattdessen geht.
      */}
      {tab === 'zahlungen' && (
        <Payments
          session={session}
          aufBeleg={(id) => {
            setSprungBeleg(id)
            setTab('rechnungen')
          }}
          aufListe={springe}
        />
      )}
    </>
  )

  /*
    Zwei Rahmen, eine Anwendung.

    Der Superuser arbeitet am Laptop und bekommt die Büroansicht mit
    Seitenleiste – so, wie er es aus der bisherigen Software kennt. Der
    Monteur behält die bestehende schmale Oberfläche: sie ist für eine Hand
    mit Handschuh gebaut, und eine Seitenleiste wäre dort im Weg.

    Die Bildschirme darin sind dieselben. Nur der Rahmen unterscheidet sich.
  */
  /*
    Paket 12 · Der Betreiber bekommt einen eigenen Rahmen.

    Bis hierher war das Betreibercockpit ein Reiter IM Büro – mit der
    Seitenleiste des Handwerksbetriebs daneben: Rechnungen, Offerten,
    Rapporte, Kunden, Leistungen. Für den Plattformbetreiber ist das die
    falsche Informationsarchitektur; er führt keinen dieser Vorgänge aus.

    Die Control Plane ersetzt den Büro-Rahmen deshalb vollständig, statt in
    ihm zu wohnen. Zurück ins eigene Büro geht es über «Zu meinem Betrieb» –
    zwei Rollen, zwei Rahmen, ein Konto.

    Geschützt ist das wie zuvor am Server: `betreiber` kommt aus `GET /me`,
    und jede Route prüft zusätzlich selbst. Die Oberfläche nimmt kein Recht
    vorweg.
  */
  if (betreiber && tab === 'betreiber') {
    return (
      <ControlPlane
        benutzer={session.name ?? session.email ?? ''}
        aufBuero={() => setTab(superuser ? 'dashboard' : 'start')}
        aufAbmelden={onLogout}
      />
    )
  }

  if (superuser) {
    return (
      <OfficeShell
        aktiv={tab}
        aufWechsel={ausMenue}
        logoKey={logoKey}
        firma={firma}
        benutzer={session.name ?? session.email ?? ''}
        aufAbmelden={onLogout}
        aufStartseite={aufStartseite}
        hinweise={hinweiszahl}
        betreiber={betreiber}
        betreuung={betreuung}
        /*
          Der Eintrag «Kita-Gemeinschaft» steht genau dort, wo er etwas
          bedeutet: bei einer Kita. Dieselbe Auskunft, die den Reiter
          «Betreuung» einschaltet – kein zweiter Abruf. Für jede andere
          Branche fehlt der Eintrag, und wer die Adresse errät, bekommt am
          Server 404, weil sein Betrieb keiner Gemeinschaft angehört.
        */
        gemeinschaft={betreuung}
        /*
          Die beiden Handgriffe, die der Rahmen nicht von selbst kennt.

          Sie stehen hier, weil nur App weiss, wie eine Express-Rechnung
          entsteht – der Rahmen kennt Bereiche, keine Formulare. Alles
          Weitere ergänzt die Befehlszeile aus der Bereichsliste selbst.
        */
        befehle={[
          {
            id: 'neu-rechnung',
            wort: 'Express-Rechnung',
            gruppe: 'Anlegen',
            icon: 'rechnung',
            tun: () => setTab('express')
          },
          {
            id: 'neu-offerte',
            wort: 'Express-Offerte',
            gruppe: 'Anlegen',
            icon: 'offerte',
            tun: () => setTab('expressofferte')
          }
        ]}
      >
        {/*
          Der Ausgangskorb gehört auch ins Büro.

          Die ConnectionBar hing bisher nur am Monteur-Rahmen (`<Shell bar>`).
          Ein Eintrag des Superusers, den der Server abgewiesen hat – ein
          Rapport, eine Zuweisung, eine Änderung –, landete damit als «failed»
          im Ausgangskorb, und niemand sah es je. Die Handlung war weg, und die
          Oberfläche sagte nichts. Genau so verschwindet Arbeit in einer
          Vorführung.
        */}
        <ConnectionBar />
        {inhalt}
      </OfficeShell>
    )
  }

  return (
    <Shell
      bar
      nav={
        <div className="tabs-wrap">
          <nav className="tabs" aria-label="Bereiche">
            {visible.map((t) => (
              <button
                key={t.id}
                id={`tab-${t.id}`}
                type="button"
                className={t.id === tab ? 'tab active' : 'tab'}
                aria-current={t.id === tab ? 'page' : undefined}
                onClick={(e) => {
                  setTab(t.id)
                  e.currentTarget.scrollIntoView({ block: 'nearest', inline: 'center' })
                }}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>
      }
    >
      {inhalt}
    </Shell>
  )
}

interface CacheCounts {
  customers: number
  projects: number
  catalogItems: number
  outbox: number
  photos: number
  changeSeq: number
}

/**
 * Status ist kein Dashboard, sondern die Werkbank für den Feldtest: wie viel
 * liegt lokal, wie viel wartet, wo steht der Zeiger. Ohne diese Zahlen ist
 * "es hat nicht synchronisiert" eine Vermutung statt eines Befunds.
 */
function Status({
  session,
  onLogout
}: {
  session: SessionUser
  onLogout: () => Promise<void>
}): JSX.Element {
  const [cache, setCache] = useState<CacheCounts | null>(null)
  const [status, setStatus] = useState<SyncStatus | null>(null)

  useEffect(() => subscribe(setStatus), [])

  const refresh = useCallback(async () => {
    setCache({
      customers: await db.customers.count(),
      projects: await db.projects.count(),
      catalogItems: await db.catalogItems.count(),
      outbox: await db.outbox.where('status').anyOf('local', 'waiting', 'sending').count(),
      photos: await db.photos.count(),
      changeSeq: await getMeta<number>(META_KEYS.changeSeq, 0)
    })
  }, [])

  useEffect(() => {
    void refresh()
  }, [refresh, status])

  /*
    Hier stand eine zweite Fassung des Abmeldens – und sie war die
    vollständige, während der Knopf in der Seitenleiste eine halbe benutzte.
    Zwei Wege zum selben Ziel, von denen einer das Cookie stehen liess: der
    Fehler ist nicht, dass es zwei waren, sondern dass niemand sehen konnte,
    welcher welcher ist. Jetzt gibt es einen, und er liegt in App().
  */

  return (
    <>
      <div className="card">
        <h1>{session.name}</h1>
        <dl>
          <dt>Betrieb</dt>
          <dd>{session.tenantName}</dd>
          <dt>Rolle</dt>
          <dd>
            <span className={`pill ${session.role}`}>
              {session.role === 'superuser' ? 'Superuser' : 'Mitarbeiter'}
            </span>
          </dd>
          <dt>Offline gültig bis</dt>
          <dd>{new Date(session.entitlementExpiresAt).toLocaleString('de-CH')}</dd>
        </dl>
      </div>

      <div className="card">
        <h2>Lokaler Bestand</h2>
        {cache ? (
          <dl className="counts">
            <dt>Kunden</dt>
            <dd>{cache.customers}</dd>
            <dt>Projekte</dt>
            <dd>{cache.projects}</dd>
            <dt>Leistungen</dt>
            <dd>{cache.catalogItems}</dd>
            <dt>Ausgangskorb</dt>
            <dd>{cache.outbox}</dd>
            <dt>Fotos</dt>
            <dd>{cache.photos}</dd>
            <dt>Zeiger</dt>
            <dd className="mono">{cache.changeSeq}</dd>
          </dl>
        ) : (
          <p className="muted">Wird geladen …</p>
        )}
        <ActionButton
          id="btn-sync"
          variant="secondary"
          labels={{ idle: 'Jetzt abgleichen', busy: 'Gleicht ab …', success: 'Aktuell' }}
          onAction={async () => {
            await syncNow({ force: true })
            await refresh()
          }}
          onError={(err) => notifyError(err)}
        />
      </div>

      <div className="card">
        <ActionButton
          variant="quiet"
          labels={{ idle: 'Abmelden', busy: 'Melde ab …' }}
          onAction={onLogout}
          onError={(err) => notifyError(err)}
        />
      </div>
    </>
  )
}
